import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { useAuth } from '../contexts/AuthContext';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Input } from '../components/ui';
import { SuccessMessages } from '../constants/messages';

/**
 * Second step of login when the user has MFA enabled.
 *
 * Reached via /login/mfa after a successful POST /auth/login that returned
 * `requires_mfa: true`. The pending mfa_token is stashed in sessionStorage
 * by AuthContext.login(); we just collect the TOTP / backup code and call
 * AuthContext.completeMfaLogin().
 */
const MfaVerifyPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const { completeMfaLogin, hasPendingMfa, cancelMfa } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No pending MFA token → not supposed to be here.
  if (!hasPendingMfa()) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await completeMfaLogin(code.trim());
      toast.success(SuccessMessages.LOGIN_SUCCESS);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      Logger.error('MFA verification failed', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 401 ? t('mfa.invalidCode') : t('mfa.verificationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = () => {
    cancelMfa();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('mfa.verifyTitle')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('mfa.verifySubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={onSubmit}>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={8}
              label={t('mfa.codeLabel') as string}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={error ?? undefined}
              className="text-center tracking-widest text-lg font-mono"
            />

            <p className="text-xs text-text-secondary">
              {t('mfa.backupCodeHint')}
            </p>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={submitting}
                disabled={submitting || code.length < 6}
                className="w-full"
              >
                {t('mfa.verifyButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={onCancel}
                className="w-full"
                disabled={submitting}
              >
                {t('mfa.cancelButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MfaVerifyPage;
